import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import withChartExport from '../../../components/ChartExporting';

const highchartUtils = require('../../../util/highcharts')(Highcharts);

import { saveConfig, updateAvgChart } from '../../../components/Filter/FilterActions';
import dateUtils from '../../../util/dates';

import expandIcon from '../../../assets/images/zoom-icon-off.svg';
import collapseIcon from '../../../assets/images/zoom-icon-on.svg';

const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff'
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: ['#00a2e3', '#007baa'],
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {
      let tooltipHtml = '',
        s = [];
      this.points.forEach(point => {
        tooltipHtml += `<div className="tooltip_label_item" style="margin: 4px 5px 1px 0; line-height: 20px;"><div style="position: relative; top: 0px; display: inline-block; width: 8px; height: 8px; margin-left: 4px; margin-right: 8px; transform: rotate(45deg); background: ${
          point.color
        }"></div>${point.series.name} rate: ${point.point.y.toFixed(2)} item${point.point.y != 1 ? 's' : ''}/day</div>`;
      });
      s.push(tooltipHtml);
      return s;
    }
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    showFirstLabel: true,
    showLastTickLabel: true,
    title: {
      text: null
    },
    plotLines: [
      {
        value: 0
      }
    ]
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      marker: {
        symbol: 'diamond',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      data: []
    }
  ]
};
const DEFAULT_POS = { x: 95, y: 85 };

export class ArrivalDepChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isFullSreen: false,
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  static defaultProps = {
    data: {}
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };

    this.chart = new Highcharts['Chart'](`arrival_dep_chart_container`, config);

    if (this.props.data && this.props.data.length) {
      this.updateDateFormat();
      this.calculateChartData(this.props.dateFormat);
    }
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.calculateChartData();
    }
    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateDateFormat();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateDateFormat() {
    const format = dateUtils.createHighchartFormats(this.props.dateFormat);
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: {
          millisecond: format.month,
          second: format.month,
          minute: format.month,
          hour: format.month,
          day: format.month,
          week: format.month,
          month: format.month
        }
      }
    });
  }

  calculateChartData() {
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (!this.props.data.length) {
      return false;
    }

    this.props.data.forEach(d => {
      this.chart.addSeries(d, false);
    });

    this.chart.update({
      xAxis: {
        plotLines: [
          {
            color: '#ececec',
            width: 1,
            value: this.props.data[0].data[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
    this.chart.redraw();
  }

  updateFontSize = () => {
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen
      },
      yAxis: {
        gridLineWidth: +this.state.isFullSreen,
        labels: {
          enabled: this.state.isFullSreen
        }
      }
    });

    this.chart.reflow();
  };

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullSreen: false,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize()
      );
    } else {
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullSreen: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box long_title ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{
            width: this.state.width,
            height: this.state.height,
            left: this.state.left,
            position: this.state.isFullSreen ? 'absolute' : ''
          }}
        >
          <h3>Arrival & Throughput Rates</h3>
          <Button
            variant='secondary'
            className={`btn-close resize ${this.state.isFullSreen ? 'fullscreen' : ''}`}
            onClick={this.handleResize}
          >
            <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
          </Button>
          <Button className="btn-close" onClick={this.handleClose}>
            &times;
          </Button>
          <div id="arrival_dep_chart_container" />
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateAvgChart(false, 'cfd')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    dateFormat: state.filters.dateFormat
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(ArrivalDepChart));
